import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { useTranslation } from "react-i18next";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { colors } from './Theme/WebTheme';
import recruitmentImage from '../assets/img/White Yellow Modern Business Quote LinkedIn Post (Publication Instagram) (1).png';
import { keyframes } from '@mui/system';

const DriverRecruitmentBanner = () => {
  const { t, i18n } = useTranslation();

  const content = {
    fr: {
      title: "Chauffeurs VTC, Rejoignez-nous !",
      subtitle: "Bénéficiez d'avantages exclusifs avec notre partenaire Eden Auto Montauban - Toyota Business",
      benefits: [
        {
          icon: <DirectionsCarIcon />,
          title: "Véhicules hybrides Toyota",
          description: "Offres véhicules et entretien proposées avec Eden Auto Montauban"
        },
        {
          icon: <LocalOfferIcon />,
          title: "Remises Exceptionnelles",
          description: "10% de remise sur chaque vente + 10% sur l'entretien chez Eden Auto"
        },
        {
          icon: <HandshakeIcon />,
          title: "Accompagnement Eden Auto",
          description: "Support dédié et conditions préférentielles de notre partenaire"
        }
      ],
      cta: "Nous Contacter",
      details: "Tarifs préférentiels Eden Auto Montauban sur les modèles AYGO X, YARIS CROSS, COROLLA TOURING et C-HR",
      partner: "En partenariat avec Eden Auto Montauban"
    },
    en: {
      title: "VTC Drivers, Join Us!",
      subtitle: "Enjoy exclusive benefits with our partner Eden Auto Montauban - Toyota Business",
      benefits: [
        {
          icon: <DirectionsCarIcon />,
          title: "Premium Toyota Hybrid Vehicles",
          description: "100% hybrid fleet with manufacturer warranty via Eden Auto Montauban"
        },
        {
          icon: <LocalOfferIcon />,
          title: "Exceptional Discounts",
          description: "10% discount on each sale + 10% on maintenance at Eden Auto"
        },
        {
          icon: <HandshakeIcon />,
          title: "Eden Auto Support",
          description: "Dedicated support and preferential conditions from our partner"
        }
      ],
      cta: "Contact Us to Join",
      details: "Preferential rates from Eden Auto Montauban on AYGO X, YARIS CROSS, COROLLA TOURING and C-HR models",
      partner: "In partnership with Eden Auto Montauban"
    }
  };

  const currentContent = content[i18n.language] || content.fr;

  // Animation float légère
  const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  `;

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
        py: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header with Image */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h2"
              sx={{
                color: '#D4AF37',
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              {currentContent.title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontSize: { xs: '1rem', md: '1.25rem' },
                fontWeight: 400,
                maxWidth: '800px',
                opacity: 0.9,
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              {currentContent.subtitle}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={recruitmentImage}
              alt="Rejoignez-nous"
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: '280px',
                margin: { xs: '0 auto', md: '0 0 0 auto' },
                display: 'block',
                borderRadius: '16px',
                boxShadow: '0 10px 40px rgba(212, 175, 55, 0.3)',
                animation: `${floatAnimation} 3s ease-in-out infinite`
              }}
            />
          </Grid>
        </Grid>

        {/* Benefits Grid */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {currentContent.benefits.map((benefit, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderRadius: '16px',
                  padding: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    borderColor: 'rgba(212, 175, 55, 0.4)',
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(212, 175, 55, 0.2)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                    border: '2px solid #D4AF37',
                    mb: 2,
                    '& svg': {
                      fontSize: '2rem',
                      color: '#D4AF37'
                    }
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    mb: 1,
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {benefit.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Toyota Models Details */}
        <Box
          sx={{
            backgroundColor: 'rgba(212, 175, 55, 0.05)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: '12px',
            padding: { xs: 2, md: 3 },
            mb: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: '0.875rem', md: '1rem' },
              lineHeight: 1.6
            }}
          >
            {currentContent.details}
          </Typography>
        </Box>

        {/* Partner Badge */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            {currentContent.partner}
          </Typography>
        </Box>

        {/* CTA Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            href="/contact-us"
            sx={{
              backgroundColor: colors.Button_Primary,
              color: '#000',
              fontWeight: 700,
              fontSize: { xs: '1rem', md: '1.1rem' },
              padding: { xs: '12px 32px', md: '16px 48px' },
              borderRadius: '50px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 8px 30px rgba(212, 175, 55, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#f4d03f',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(212, 175, 55, 0.6)'
              }
            }}
          >
            {currentContent.cta}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DriverRecruitmentBanner;
